<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import { timeZoneOptions } from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';

const props = defineProps({
  assistant: { type: Object, default: () => ({}) },
  submitLabel: { type: String, required: true },
  showCancel: { type: Boolean, default: true },
  showSchedule: { type: Boolean, default: false },
});

const emit = defineEmits(['submit', 'cancel', 'dirty']);
const { t } = useI18n();

const state = reactive({
  name: '',
  description: '',
  memories: true,
  faqsFromResolved: false,
  scheduleSource: 'assistant',
  timeZone: '',
});

const timeZones = timeZoneOptions();
const sources = computed(() => [
  {
    id: 'channel',
    label: t('ARROWS_AI.ASSISTANTS.FORM.SCHEDULE.CHANNEL.LABEL'),
    description: t('ARROWS_AI.ASSISTANTS.FORM.SCHEDULE.CHANNEL.DESCRIPTION'),
  },
  {
    id: 'assistant',
    label: t('ARROWS_AI.ASSISTANTS.FORM.SCHEDULE.ASSISTANT.LABEL'),
    description: t('ARROWS_AI.ASSISTANTS.FORM.SCHEDULE.ASSISTANT.DESCRIPTION'),
  },
]);

const snapshot = ref('');

const serialize = () =>
  JSON.stringify({
    name: state.name,
    description: state.description,
    memories: state.memories,
    faqsFromResolved: state.faqsFromResolved,
    scheduleSource: state.scheduleSource,
    timeZone: state.timeZone,
  });

const getPayload = () => ({
  name: state.name,
  description: state.description,
  features: {
    memories: state.memories,
    citations: false,
    faqsFromResolved: state.faqsFromResolved,
  },
  ...(props.showSchedule && {
    scheduleSource: state.scheduleSource,
    timeZone: state.timeZone,
  }),
});

watch(
  () => props.assistant,
  next => {
    state.name = next?.name || '';
    state.description = next?.description || '';
    state.memories = next?.features?.memories !== false;
    state.faqsFromResolved = Boolean(next?.features?.faqsFromResolved);
    state.scheduleSource = next?.scheduleSource || 'assistant';
    state.timeZone = next?.timeZone || '';
    snapshot.value = serialize();
  },
  { immediate: true }
);

const isDirty = computed(() => serialize() !== snapshot.value);

watch(isDirty, value => emit('dirty', value), { immediate: true });

const handleSubmit = () => {
  emit('submit', getPayload());
};

defineExpose({ isDirty, getPayload });
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.name"
      :label="t('ARROWS_AI.ASSISTANTS.FORM.NAME')"
      :placeholder="t('ARROWS_AI.ASSISTANTS.FORM.NAME_PLACEHOLDER')"
    />
    <label class="flex flex-col gap-2 text-sm text-n-slate-12">
      {{ t('ARROWS_AI.ASSISTANTS.FORM.DESCRIPTION') }}
      <textarea
        v-model="state.description"
        rows="5"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
        :placeholder="t('ARROWS_AI.ASSISTANTS.FORM.DESCRIPTION_PLACEHOLDER')"
      />
    </label>
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1">
        <span class="font-medium text-n-slate-12">
          {{ t('ARROWS_AI.ASSISTANTS.FORM.MEMORIES.LABEL') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('ARROWS_AI.ASSISTANTS.FORM.MEMORIES.DESCRIPTION') }}
        </span>
      </div>
      <Switch v-model="state.memories" />
    </div>
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1">
        <span class="font-medium text-n-slate-12">
          {{ t('ARROWS_AI.ASSISTANTS.FORM.FAQS_FROM_RESOLVED.LABEL') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('ARROWS_AI.ASSISTANTS.FORM.FAQS_FROM_RESOLVED.DESCRIPTION') }}
        </span>
      </div>
      <Switch v-model="state.faqsFromResolved" />
    </div>
    <div v-if="showSchedule" class="flex flex-col gap-3">
      <span class="font-medium text-n-slate-12">
        {{ t('ARROWS_AI.ASSISTANTS.FORM.SCHEDULE.LABEL') }}
      </span>
      <RadioCard
        v-for="source in sources"
        :id="source.id"
        :key="source.id"
        :label="source.label"
        :description="source.description"
        :is-active="state.scheduleSource === source.id"
        active-outline="outline-n-iris-9"
        @select="state.scheduleSource = $event"
      />
      <div
        v-if="state.scheduleSource === 'assistant'"
        class="flex flex-col gap-2"
      >
        <span class="text-sm text-n-slate-12">
          {{ t('ARROWS_AI.ASSISTANTS.FORM.SCHEDULE.TIME_ZONE') }}
        </span>
        <ComboBox
          v-model="state.timeZone"
          :options="timeZones"
          :placeholder="t('ARROWS_AI.ASSISTANTS.FORM.SCHEDULE.NO_TIME_ZONE')"
          :search-placeholder="t('ARROWS_AI.ASSISTANTS.FORM.SCHEDULE.SEARCH')"
        />
        <span class="text-sm text-n-slate-11">
          {{ t('ARROWS_AI.ASSISTANTS.FORM.SCHEDULE.HINT') }}
        </span>
      </div>
    </div>
    <div class="flex items-center w-full gap-3">
      <Button
        v-if="showCancel"
        type="button"
        variant="faded"
        color="slate"
        class="w-full"
        :label="t('ARROWS_AI.CANCEL')"
        @click="emit('cancel')"
      />
      <Button type="submit" class="w-full" :label="submitLabel" />
    </div>
  </form>
</template>
